import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Sparkles, AlertCircle, Zap } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import {
  FUNDAMENTAL_MODES,
  type FundamentalExerciseOutput,
  type FundamentalMode,
} from '@jeraisonne/ai';
import {
  usePublishedChapters,
  useChapterLessons,
  useChapterCompetences,
} from '@/lib/admin/exercise-queries';
import { useCreateFundamentalExercise } from '@/lib/admin/fundamental-mutations';
import {
  generateFundamentalExercise,
  generateErrorMessage,
  GenerateError,
} from '@/lib/exercises/fundamental-client';
import { FundamentalExerciseEditor } from '@/components/fundamental-exercise-editor';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const COLLEGE_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle === 'collège');
const LYCEE_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle === 'lycée');

const MODE_LABEL: Record<FundamentalMode, string> = {
  LESSON_RECALL: 'Rappel de cours',
  CALCULATION: 'Calcul direct',
  FORMULA: 'Formules',
  THEOREM: 'Théorèmes',
  MIXED: 'Mixte',
};

/**
 * Flux de création d'un exercice fondamental (banque de questions Warmup).
 * Sélection niveau/chapitre/leçon → génération IA dédiée → édition → sauvegarde.
 */
export function FundamentalExerciseFlow() {
  const navigate = useNavigate();
  const { data: chapters, isLoading: chaptersLoading } = usePublishedChapters();
  const createFundamental = useCreateFundamentalExercise();

  const [selectedLevel, setSelectedLevel] = useState<SchoolLevel | ''>('');
  const [chapterId, setChapterId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const { data: lessons } = useChapterLessons(chapterId);
  const { data: competences = [] } = useChapterCompetences(chapterId);

  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [questionCount, setQuestionCount] = useState(10);
  const [mode, setMode] = useState<FundamentalMode>('MIXED');
  const [customInstructions, setCustomInstructions] = useState('');

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [preview, setPreview] = useState<FundamentalExerciseOutput | null>(null);

  const selectedChapter = chapters?.find((c) => c.id === chapterId) ?? null;
  const filteredChapters = (chapters ?? []).filter((c) => c.level === selectedLevel);

  function handleLevelSelect(l: SchoolLevel) {
    if (l !== selectedLevel) {
      setSelectedLevel(l);
      setChapterId('');
      setLessonId('');
    }
  }

  async function handleGenerate() {
    if (!chapterId || !selectedChapter) return;
    setGenerating(true);
    setError(null);
    setErrorDetail(null);
    try {
      const result = await generateFundamentalExercise({
        chapter_id: chapterId,
        lesson_id: lessonId || null,
        subject: selectedChapter.subject as SubjectSlug,
        level: selectedLevel as SchoolLevel,
        difficulty,
        question_count: questionCount,
        mode,
        custom_instructions: customInstructions || undefined,
      });
      setPreview(result.output);
    } catch (err) {
      if (err instanceof GenerateError) {
        setError(generateErrorMessage(err.code));
        setErrorDetail(
          `[${err.code}] ${err.message}${err.rawOutput ? '\n\n' + err.rawOutput.slice(0, 800) : ''}`,
        );
      } else {
        setError('Erreur inattendue. Réessaie.');
        setErrorDetail(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleSave(status: 'draft' | 'published') {
    if (!preview || !chapterId) return;
    try {
      const id = await createFundamental.mutateAsync({
        chapter_id: chapterId,
        lesson_id: lessonId || null,
        status,
        fundamental: preview,
      });
      navigate({ to: '/tutor/exercises/$exerciseId', params: { exerciseId: id } });
    } catch {
      // erreur affichée via createFundamental.error
    }
  }

  const canGenerate = !!selectedLevel && !!chapterId && !generating;

  return (
    <div className="space-y-5">
      <div className="bg-primary-50 border-primary-100 flex items-start gap-2.5 rounded-xl border px-4 py-3">
        <Zap className="text-primary-600 mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-primary-800 font-sans text-[13px]">
          Un exercice fondamental est une banque de questions QCM <strong>indépendantes</strong> qui
          alimente le mode <strong>Warmup</strong> des élèves. Il est{' '}
          <strong>toujours gratuit</strong>.
        </p>
      </div>

      {/* Niveau */}
      <div>
        <label className="font-display text-ink-700 mb-2 block text-[12px] font-semibold">
          Niveau <span className="text-error-500">*</span>
        </label>
        <div className="space-y-2">
          <p className="text-ink-400 font-sans text-[11px] uppercase tracking-wide">Collège</p>
          <div className="flex flex-wrap gap-2">
            {COLLEGE_LEVELS.map((l) => (
              <LevelPill
                key={l.value}
                l={l}
                active={selectedLevel === l.value}
                onClick={() => handleLevelSelect(l.value)}
              />
            ))}
          </div>
          <p className="text-ink-400 pt-1 font-sans text-[11px] uppercase tracking-wide">Lycée</p>
          <div className="flex flex-wrap gap-2">
            {LYCEE_LEVELS.map((l) => (
              <LevelPill
                key={l.value}
                l={l}
                active={selectedLevel === l.value}
                onClick={() => handleLevelSelect(l.value)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Chapitre */}
      {selectedLevel && (
        <div>
          <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
            Chapitre <span className="text-error-500">*</span>
          </label>
          {chaptersLoading ? (
            <div className="bg-ink-100 h-11 animate-pulse rounded-xl" />
          ) : filteredChapters.length === 0 ? (
            <p className="text-ink-400 font-sans text-sm italic">
              Aucun chapitre publié pour ce niveau.
            </p>
          ) : (
            <select
              value={chapterId}
              onChange={(e) => {
                setChapterId(e.target.value);
                setLessonId('');
              }}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full appearance-none rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2"
            >
              <option value="">Sélectionner un chapitre…</option>
              {SUBJECTS.map((s) => {
                const subjectChapters = filteredChapters.filter((c) => c.subject === s.value);
                if (!subjectChapters.length) return null;
                return (
                  <optgroup key={s.value} label={s.label}>
                    {subjectChapters.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          )}
          {selectedChapter && (
            <p className="text-ink-500 mt-1 font-sans text-xs">
              {SUBJECT_LABEL[selectedChapter.subject] ?? selectedChapter.subject}
            </p>
          )}
        </div>
      )}

      {/* Leçon */}
      {chapterId && (
        <div>
          <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
            Leçon <span className="text-ink-400 font-normal">(recommandé)</span>
          </label>
          {!lessons || lessons.length === 0 ? (
            <p className="text-ink-400 font-sans text-sm italic">
              Aucune leçon publiée pour ce chapitre.
            </p>
          ) : (
            <select
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full appearance-none rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2"
            >
              <option value="">Aucune leçon associée</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Paramètres de génération */}
      {chapterId && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
              Nombre de questions
            </label>
            <input
              type="number"
              min={1}
              max={40}
              value={questionCount}
              onChange={(e) =>
                setQuestionCount(Math.min(40, Math.max(1, Number(e.target.value) || 10)))
              }
              className="border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2"
            />
          </div>
          <div>
            <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
              Difficulté
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full appearance-none rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2"
            >
              <option value="easy">Facile</option>
              <option value="medium">Moyen</option>
              <option value="hard">Difficile</option>
            </select>
          </div>
          <div>
            <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
              Type pédagogique
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as FundamentalMode)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full appearance-none rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2"
            >
              {FUNDAMENTAL_MODES.map((m) => (
                <option key={m} value={m}>
                  {MODE_LABEL[m]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {chapterId && (
        <div>
          <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
            Instructions personnalisées{' '}
            <span className="text-ink-400 font-normal">(facultatif)</span>
          </label>
          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Ex : insiste sur les conversions d'unités, évite les fractions trop complexes…"
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>
      )}

      <Button
        onClick={handleGenerate}
        disabled={!canGenerate}
        className="flex w-full items-center gap-2 sm:w-auto"
      >
        {generating ? (
          <>
            <span className="animate-spin">⏳</span> Génération en cours…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" /> {preview ? 'Régénérer' : 'Générer les questions'}
          </>
        )}
      </Button>

      {error && (
        <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
          <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-error-700 font-sans text-sm">{error}</p>
            {errorDetail && (
              <pre className="text-error-600/80 mt-1.5 overflow-auto whitespace-pre-wrap break-all font-mono text-xs">
                {errorDetail}
              </pre>
            )}
          </div>
        </div>
      )}

      {createFundamental.error && (
        <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
          <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-error-700 font-sans text-sm">{createFundamental.error.message}</p>
        </div>
      )}

      {preview && (
        <FundamentalExerciseEditor
          value={preview}
          onChange={setPreview}
          onPublish={handleSave}
          onRegenerate={handleGenerate}
          pending={generating || createFundamental.isPending}
          competences={competences}
        />
      )}
    </div>
  );
}

function LevelPill({
  l,
  active,
  onClick,
}: {
  l: { value: SchoolLevel; label: string };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-9 rounded-full border px-4 font-sans text-sm font-medium transition-colors active:scale-[0.98]',
        active
          ? 'bg-primary-500 border-primary-500 text-white'
          : 'border-ink-200 text-ink-700 hover:border-primary-300 hover:text-primary-600 bg-white',
      )}
    >
      {l.label}
    </button>
  );
}
